import React, { useEffect, useRef, useState } from 'react';
import * as d3 from 'd3';
import './Chart.css';
import { getTouchpointColorScale } from '../utils/touchpointColors';

const PrevalenceJitterPlot = ({ allCountyData, stateData, selectedCountyFips, touchpointTypes, selectedYear, onCountySelect }) => {
  const svgRef = useRef();
  const containerRef = useRef();
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });

  useEffect(() => {
    if (!containerRef.current) return;

    const updateDimensions = () => {
      if (containerRef.current) {
        setDimensions({
          width: containerRef.current.clientWidth,
          height: containerRef.current.clientHeight
        });
      }
    };

    updateDimensions();
    const resizeObserver = new ResizeObserver(updateDimensions);
    resizeObserver.observe(containerRef.current);

    return () => resizeObserver.disconnect();
  }, []);

  useEffect(() => {
    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    if (!allCountyData || !selectedYear || dimensions.width === 0) {
      return;
    }

    const plotData = [];
    
    // Helper for consistent jitter
    const seededRandom = (seed) => {
      const x = Math.sin(seed) * 10000;
      return x - Math.floor(x);
    };

    // Collect all prevalence values for each type to calculate stats
    const typeValues = {};
    touchpointTypes.forEach(type => typeValues[type] = []);

    for (const fips in allCountyData) {
      const county = allCountyData[fips];
      const totalDecedents = county.touchpoints['All']?.[selectedYear]?.['Decedent Total Individual Count'];

      if (totalDecedents > 0) {
        touchpointTypes.forEach((type, typeIndex) => {
          const touchpointDecedents = county.touchpoints[type]?.[selectedYear]?.['Decedent Touchpoint Individual Count'] || 0;
          const prevalence = (touchpointDecedents / totalDecedents) * 100;
          
          typeValues[type].push(prevalence);

          // Deterministic jitter based on fips and type
          const seed = parseInt(fips, 10) * 1000 + typeIndex;
          const jitter = (seededRandom(seed) - 0.5);

          plotData.push({
            fips: county.fips,
            countyName: county.name,
            type: type,
            prevalence: prevalence,
            jitter: jitter,
          });
        });
      }
    }

    // Calculate Box Plot Statistics (Min, Q1, Median, Q3, Max)
    const categoryStats = {};
    touchpointTypes.forEach(type => {
      const values = typeValues[type].sort(d3.ascending);
      if (values.length === 0) return;

      const q1 = d3.quantile(values, 0.25);
      const median = d3.quantile(values, 0.5);
      const q3 = d3.quantile(values, 0.75);
      const min = d3.min(values);
      const max = d3.max(values);
      
      categoryStats[type] = { min, q1, median, q3, max };
    });

    const margin = { top: 20, right: 20, bottom: 80, left: 50 };
    const width = dimensions.width - margin.left - margin.right;
    const height = dimensions.height - margin.top - margin.bottom;

    svg.attr('width', width + margin.left + margin.right)
       .attr('height', height + margin.top + margin.bottom);
    
    const g = svg.append('g').attr('transform', `translate(${margin.left},${margin.top})`);

    // 3. Scales
    // X-axis: Categories (Touchpoint Types)
    const x = d3.scaleBand()
      .domain(touchpointTypes)
      .range([0, width])
      .padding(0.4);

    // Y-axis: Prevalence (0-100%)
    const y = d3.scaleLinear()
      .domain([0, 100])
      .range([height, 0]);
    
    // Color scale for the categories
    const colorScale = getTouchpointColorScale(touchpointTypes); 

    // 4. Axes
    g.append('g')
      .attr('transform', `translate(0,${height})`)
      .call(d3.axisBottom(x))
      .selectAll("text")
      .style("text-anchor", "end")
      .attr("dx", "-.8em")
      .attr("dy", ".15em")
      .attr("transform", "rotate(-30)")
      .style("font-size", "10px");

    g.append('g')
      .call(d3.axisLeft(y).ticks(5).tickFormat(d => `${d}%`))
      .selectAll("text")
      .style("font-size", "12px");

    // 5. Draw Box Plots
    touchpointTypes.forEach(type => {
        const stats = categoryStats[type];
        if (!stats) return;

        const center = x(type) + x.bandwidth() / 2;
        const boxWidth = x.bandwidth(); 
        const color = colorScale(type);

        // Dashed line (Whisker range - Vertical)
        g.append("line")
            .attr("x1", center)
            .attr("x2", center)
            .attr("y1", y(stats.min))
            .attr("y2", y(stats.max))
            .attr("stroke", color)
            .style("stroke-dasharray", ("3, 3"));

        // Whisker Caps (Horizontal lines at min/max)
        g.append("line")
            .attr("x1", center - boxWidth/4).attr("x2", center + boxWidth/4)
            .attr("y1", y(stats.min)).attr("y2", y(stats.min))
            .attr("stroke", color);
        
        g.append("line")
            .attr("x1", center - boxWidth/4).attr("x2", center + boxWidth/4)
            .attr("y1", y(stats.max)).attr("y2", y(stats.max))
            .attr("stroke", color);

        // Box (Q3 to Q1) - Note: y(q3) is numerically smaller (higher up) than y(q1)
        g.append("rect")
            .attr("x", center - boxWidth/2)
            .attr("y", y(stats.q3))
            .attr("height", y(stats.q1) - y(stats.q3))
            .attr("width", boxWidth)
            .attr("stroke", color)
            .style("fill", color)
            .style("opacity", 0.3);

        // Median Line
        g.append("line")
            .attr("x1", center - boxWidth/2).attr("x2", center + boxWidth/2)
            .attr("y1", y(stats.median)).attr("y2", y(stats.median))
            .attr("stroke", color)
            .attr("stroke-width", 2);
    });

    // 6. Draw Jittered Dots
    const jitterWidth = x.bandwidth(); 
    
    const dots = g.selectAll('.dot').data(plotData).join('circle')
        .attr('class', 'dot')
        .attr('cx', d => x(d.type) + x.bandwidth() / 2 + d.jitter * jitterWidth * 0.8)
        .attr('cy', d => y(d.prevalence))
        .attr('r', d => d.fips === selectedCountyFips ? 4 : 2.5)
        .style('fill', d => d.fips === selectedCountyFips ? '#2c3e50' : colorScale(d.type))
        .style('stroke', d => d.fips === selectedCountyFips ? 'black' : 'none')
        .style('fill-opacity', d => d.fips === selectedCountyFips ? 1 : 0.8)
        .style('cursor', 'pointer')
        .on('click', function(event, d) {
          if (onCountySelect) {
            onCountySelect(d.fips);
          }
        })
        .on('mouseover', function(event, d) {
          d3.select(this)
            .transition().duration(200)
            .attr('r', d.fips === selectedCountyFips ? 5.5 : 4.5)
            .style('fill-opacity', 1);
        })
        .on('mouseout', function(event, d) {
          d3.select(this)
            .transition().duration(200)
            .attr('r', d.fips === selectedCountyFips ? 4 : 2.5)
            .style('fill-opacity', d => d.fips === selectedCountyFips ? 1 : 0.8);
        });

    dots.append('title')
        .text(d => `${d.countyName}\n${d.type}\nPrevalence: ${d.prevalence.toFixed(1)}%`);

  }, [allCountyData, stateData, selectedCountyFips, touchpointTypes, selectedYear, onCountySelect, dimensions.width, dimensions.height]);

  return (
    <div ref={containerRef} className="chart-container">
      {!allCountyData && <div className="placeholder">Loading data...</div>}
      <svg ref={svgRef} />
    </div>
  );
};

export default PrevalenceJitterPlot;